import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '../i18n';

/**
 * La derniere reponse de chaque ecran qui en demande une.
 *
 * Un onglet est une route : en changer demonte l'ecran et emporte son
 * etat, si bien que revenir sur son profil rejouait le chargement depuis
 * rien — trois points sur un ecran vide, a chaque aller-retour. Garder la
 * derniere reponse permet de la reafficher tout de suite et de rafraichir
 * derriere, sans que l'ecran se vide.
 *
 * C'est un cache de session, en memoire : il ne survit pas au
 * rechargement de l'application, et il est vide des que le membre ou le
 * spot change — sans quoi on verrait un instant les donnees d'un autre.
 */
const cache = new Map<string, unknown>();

export function clearScreenCache(): void {
  cache.clear();
}

/**
 * Corriger de l'exterieur ce qu'un ecran garde en memoire.
 *
 * Publier un post se fait sur un autre ecran que le fil. Or le fil ne se
 * relit pas tout seul en revenant — c'est voulu, sans quoi le post qu'on
 * lisait glisserait sous ceux arrives entre-temps — si bien que le sien
 * n'apparaissait qu'apres un tirage. On pose donc le post dans le cache
 * avant de revenir : le fil se remonte avec, sans un aller-retour reseau.
 *
 * Ne fait rien si l'ecran n'a jamais ete charge : il ira le chercher
 * lui-meme, et il y trouvera le post.
 */
export function updateScreenCache<T>(cacheKey: string, updater: (previous: T) => T): void {
  if (!cache.has(cacheKey)) return;
  cache.set(cacheKey, updater(cache.get(cacheKey) as T));
}

/**
 * Les cles du cache qui portent sur la meme chose.
 *
 * Le fil en a une par recherche — `feed`, `feed:rooftop` — et corriger un
 * post demande de toutes les visiter : celle qu'on a sous les yeux n'est
 * pas forcement celle d'ou l'on vient. Le prefixe seul compte comme une
 * cle, le reste se separe par deux-points.
 */
export function screenCacheKeys(prefix: string): string[] {
  return [...cache.keys()].filter((cle) => cle === prefix || cle.startsWith(`${prefix}:`));
}

/**
 * Poser dans le cache ce qu'un ecran n'a pas encore demande.
 *
 * `updateScreenCache` corrige ce qui est deja connu ; celle-ci sert
 * l'inverse — repondre d'avance a une question qui va etre posee. Une
 * carte de post partagee dans une conversation se lit ainsi avec le fil
 * plutot qu'apres lui, et s'affiche pleine des le premier rendu.
 */
export function seedScreenCache<T>(cacheKey: string, value: T): void {
  cache.set(cacheKey, value);
}

/**
 * Relire ce que le cache connait deja, hors de tout ecran.
 *
 * `seedScreenCache` y depose ; celle-ci y prend. Elle sert a ce qui n'est
 * pas charge par `useAsync` mais passe de main en main — le post d'un fil
 * de commentaires, qui arrive avec la navigation. Sans elle, un ecran
 * remonte sans son etat de navigation — c'est le cas de celui qu'on
 * entrevoit derriere un geste de retour — redemandait au reseau ce qu'il
 * avait affiche une seconde plus tot.
 */
export function readScreenCache<T>(cacheKey: string): T | undefined {
  return cache.get(cacheKey) as T | undefined;
}

/**
 * Chargement de donnees pour un ecran.
 *
 * Renvoie l'etat classique donnee / chargement / erreur, plus `reload`
 * pour rejouer la requete apres une action (like, inscription, envoi...).
 * Les reponses obsoletes sont ignorees, ce qui evite les scintillements
 * quand on tape dans une barre de recherche.
 *
 * `cacheKey` vaut pour tout ecran qu'on quitte et qu'on reprend — c'est-
 * a-dire presque tous. On l'avait d'abord reserve aux ecrans d'onglet, en
 * pensant qu'un ecran de detail ne s'ouvrait qu'une fois : c'est faux. On
 * ouvre un evenement depuis « Mes evenements » et on y revient, on ouvre
 * un profil depuis un classement et on y revient — et sans cle, l'ecran
 * se vidait a chaque retour pour se remplir de ce qu'il montrait deja.
 *
 * Restent sans cle les ecrans dont le contenu depend d'une recherche en
 * cours, ou dont la fraicheur fait le sujet : la liste de gens a inviter
 * dans un groupe, le catalogue des spots.
 *
 * `keepCached` va plus loin : avec lui, revenir sur l'ecran ne relit
 * rien du tout. Le cache fait foi jusqu'a ce qu'on demande expressement
 * autre chose. C'est ce qu'il faut a un fil qu'on lit : partir voir
 * ailleurs et revenir doit remettre sous les yeux le meme post, a la
 * meme place — un rafraichissement silencieux le ferait glisser.
 */
export function useAsync<T>(
  loader: () => Promise<T>,
  deps: unknown[] = [],
  cacheKey?: string,
  keepCached = false,
): {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Relit et rend la main une fois la reponse arrivee. */
  refresh: () => Promise<void>;
  /** Met a jour la donnee sans repasser par le reseau (mise a jour optimiste). */
  setData: (updater: T | ((previous: T) => T)) => void;
} {
  // La derniere reponse connue, s'il y en a une : l'ecran s'affiche avec
  // elle des le premier rendu, avant meme que le reseau reponde.
  const known = cacheKey ? (cache.get(cacheKey) as T | undefined) : undefined;

  const [data, setData] = useState<T | null>(known ?? null);
  const [loading, setLoading] = useState(known === undefined);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // Identifie la requete en cours : seule la derniere a le droit d'ecrire.
  const requestId = useRef(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  /*
    Ce qui autorise a ne rien relire : les memes dependances que la
    derniere lecture, et aucune relecture demandee.

    On compare l'etat plutot que de compter les passages de l'effet. Le
    mode strict de React l'execute deux fois au montage, et une regle
    fondee sur « la premiere fois » aurait relu au second passage — donc
    en developpement seulement, ce qui est la pire sorte de difference.

    **La derniere lecture, et non l'arrivee.** Une dependance qui revient
    a sa valeur de depart retombait sinon dans ce raccourci sans que la
    donnee affichee, elle, soit revenue avec elle : une recherche sans
    resultat effacee du champ laissait le fil vide, avec « le feed est
    encore vide » par-dessus le marche. Le raccourci sert le retour sur un
    ecran deja lu, pas le retour d'une valeur.
  */
  const signature = JSON.stringify(deps);
  const derniereLecture = useRef(signature);

  /*
    La donnee suit sa cle.

    `known` n'est lu qu'au tout premier rendu ; quand la cle change en
    cours de vie — une recherche qu'on tape, puis qu'on efface — l'etat
    gardait la reponse de la cle precedente jusqu'a l'arrivee de la
    nouvelle. On adopte donc tout de suite ce que le cache connait de la
    nouvelle cle, quitte a n'avoir rien : c'est la seule facon de ne
    jamais montrer la reponse d'une autre question.
  */
  const cleAffichee = useRef(cacheKey);
  if (cacheKey !== cleAffichee.current) {
    cleAffichee.current = cacheKey;
    const connu = cacheKey ? (cache.get(cacheKey) as T | undefined) : undefined;
    // Forme fonctionnelle : une donnee qui serait elle-meme une fonction
    // passerait sinon pour une mise a jour.
    setData(() => connu ?? null);
    setLoading(connu === undefined);
  }

  useEffect(() => {
    if (
      keepCached &&
      cacheKey &&
      cache.has(cacheKey) &&
      signature === derniereLecture.current &&
      nonce === 0
    ) {
      setLoading(false);
      return;
    }

    derniereLecture.current = signature;
    const id = ++requestId.current;
    /*
      Avec une donnee deja sous les yeux, le rafraichissement se fait sans
      le dire : remettre `loading` a vrai reviendrait a vider l'ecran pour
      le remplir de la meme chose.
    */
    if (!cacheKey || !cache.has(cacheKey)) setLoading(true);
    setError(null);

    loaderRef
      .current()
      .then((result) => {
        if (id !== requestId.current) return;
        if (cacheKey) cache.set(cacheKey, result);
        setData(result);
      })
      .catch((cause: unknown) => {
        if (id !== requestId.current) return;
        /*
          Un rafraichissement silencieux qui echoue reste silencieux : le
          contenu affiche est celui du dernier passage, il vaut mieux que
          rien. L'erreur ne prend l'ecran que s'il n'y avait rien a
          montrer.
        */
        if (cacheKey && cache.has(cacheKey)) return;
        setError(cause instanceof Error ? cause.message : t.commun.erreur);
      })
      .finally(() => {
        if (id !== requestId.current) return;
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  /*
    Relire, et savoir quand c'est fini.

    `reload` se contente de reveiller l'effet : il rend la main tout de
    suite, bien avant la reponse. Le geste de tirage, lui, doit tenir son
    indicateur jusqu'au bout — sans quoi il tourne un dixieme de seconde
    et le contenu change apres, sans qu'on fasse le lien.

    Un echec reste silencieux : ce qui est affiche vaut mieux qu'un ecran
    d'erreur pour un geste qu'on peut simplement refaire.
  */
  const refresh = useCallback(async () => {
    const id = ++requestId.current;
    try {
      const result = await loaderRef.current();
      if (id !== requestId.current) return;
      if (cacheKey) cache.set(cacheKey, result);
      setData(result);
      setError(null);
    } catch {
      /* silencieux, voir plus haut */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const update = useCallback(
    (updater: T | ((previous: T) => T)) => {
      setData((previous) => {
        const next =
          typeof updater === 'function'
            ? previous === null
              ? previous
              : (updater as (previous: T) => T)(previous)
            : updater;
        // Un like pose a l'ecran doit se retrouver au retour sur l'onglet,
        // sans quoi il clignoterait le temps que le reseau le confirme.
        if (cacheKey && next !== null) cache.set(cacheKey, next);
        return next;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [cacheKey],
  );

  return { data, loading, error, reload, refresh, setData: update };
}

/**
 * Retarde une valeur : utilise pour ne pas interroger l'API a chaque
 * frappe dans les barres de recherche.
 */
export function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
